import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Grid,
  Stack,
  Link,
  Divider,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import TitleIcon from '@mui/icons-material/Title';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import { FormProvider, RHFTextField } from '../../../components/hook-form';
import { useApi } from '../../../hooks/useApi';
import ImageUploaderWithPreview from '../../../components/hook-form/ImageUploaderWithPreview';

// ----------------------------------------------------------------------

export default function CarrouselForm({ carrousel }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { doSave } = useApi();

  const ModelSchema = Yup.object().shape({
    title: Yup.string().required(t('Required')),
    body: Yup.string().nullable(),
  });

  const defaultValues = {
    title: '',
    body: '',
  };

  const methods = useForm({
    resolver: yupResolver(ModelSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (carrousel) {
      const fields = ['title', 'body'];
      fields.forEach((field) => setValue(field, carrousel[field]));
    }
  }, [carrousel]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (data) => {
    const formData = new FormData();
    if (data.image instanceof File || data.image instanceof Blob) {
      const options = {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      };

      const compressedFile = await imageCompression(data.image, options);
      formData.append('image', compressedFile);
    }

    formData.append('title', data.title);
    formData.append('body', data.body || '');

    const headers = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    await doSave('carrousels', formData, t('carrousel_saved'), '', carrousel, headers);
    navigate(`/dashboard/carrousel/`);
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={4}>
        {/* LEFT: Main content */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Title field */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <TitleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" color="text.secondary">{t('title')}</Typography>
              </Stack>
              <RHFTextField name="title" label={t('title')} fullWidth />
            </Box>

            {/* Description field */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <DescriptionIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" color="text.secondary">{t('description')}</Typography>
              </Stack>
              <RHFTextField name="body" label={t('description')} fullWidth multiline minRows={5} />
            </Box>

            {/* Image upload */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <ImageIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" color="text.secondary">{t('image')}</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                {t('carrousel_image_hint') || 'Sube una imagen para el carrusel. Se comprimirá automáticamente.'}
              </Typography>

              {/* Image recommendation guide */}
              <Box
                sx={{
                  bgcolor: '#f0f6ff',
                  border: '1px solid',
                  borderColor: '#d0e2ff',
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: '#032C65',
                        color: '#fff',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 14,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      i
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#032C65' }}>
                      {t('image_recommendations_title') || 'Recomendaciones de imagen'}
                    </Typography>
                  </Stack>

                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      📐 {t('recommended_dimensions') || 'Dimensiones recomendadas'}: <strong>1600 x 900 px</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ↔ {t('aspect_ratio') || 'Proporción (aspect ratio)'}: <strong>16:9</strong> ({t('horizontal_widescreen') || 'horizontal / widescreen'})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      🖼 {t('format') || 'Formato'}: <strong>JPG</strong> {t('or') || 'o'} <strong>PNG</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ⚖️ {t('max_weight') || 'Peso máximo sugerido'}: <strong>2 MB</strong>
                    </Typography>
                  </Stack>

                  {/* SVG comparison graphic */}
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    sx={{ py: 1 }}
                  >
                    {/* Correct: 16:9 horizontal */}
                    <Stack alignItems="center" spacing={0.5}>
                      <Box
                        sx={{
                          width: 80,
                          height: 45,
                          bgcolor: '#e8f5e9',
                          border: '2px solid',
                          borderColor: '#4caf50',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                        }}
                      >
                        <Typography variant="caption" fontWeight={700} sx={{ color: '#2e7d32', fontSize: 10 }}>
                          ✓
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600, fontSize: 10, textAlign: 'center' }}>
                        {t('recommended_label') || 'Recomendado 1600×900'}
                      </Typography>
                    </Stack>

                    {/* Incorrect: vertical/portrait */}
                    <Stack alignItems="center" spacing={0.5}>
                      <Box
                        sx={{
                          width: 45,
                          height: 80,
                          bgcolor: '#fff3e0',
                          border: '2px solid',
                          borderColor: '#ff9800',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="caption" fontWeight={700} sx={{ color: '#e65100', fontSize: 10 }}>
                          ✗
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600, fontSize: 10, textAlign: 'center', maxWidth: 80 }}>
                        {t('avoid_vertical_label') || 'Evitar verticales o cuadradas'}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Typography variant="caption" sx={{ color: '#e65100', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    ⚠️ {t('image_orientation_warning') || 'Las imágenes verticales o cuadradas se mostrarán recortadas. Use imágenes horizontales para mejor resultado en web y móvil.'}
                  </Typography>
                </Stack>
              </Box>

              <ImageUploaderWithPreview name="image" currentUrl={carrousel?.picture || ''} />
            </Box>
          </Stack>
        </Grid>

        {/* RIGHT: Actions panel */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 3,
              background: '#f8faff',
              position: 'sticky',
              top: 80,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              {t('actions') || 'Acciones'}
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Stack spacing={1.5}>
              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
                sx={{ background: '#032C65', '&:hover': { background: '#054a9e' }, borderRadius: 2 }}
              >
                {t('save')}
              </LoadingButton>
              <Link
                component={RouterLink}
                variant="body2"
                underline="hover"
                to="/dashboard/carrousel/"
                sx={{ textAlign: 'center', display: 'block', color: 'text.secondary' }}
              >
                {t('go_back')}
              </Link>
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
}

CarrouselForm.propTypes = {
  carrousel: PropTypes.object,
};

CarrouselForm.defaultProps = {
  carrousel: undefined,
};